import { beginAction, errorReason } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION } from '@/feedback/ontology/types';

import {
  completeOauth as generatedCompleteOauth,
  OAuthIntent as GeneratedOAuthIntent,
  OAuthProvider as GeneratedOAuthProvider,
  OAuthResponseStatus as GeneratedOAuthResponseStatus,
  prepareOauth as generatedPrepareOauth,
} from './api/generated';
import type {
  CallbackRequest,
  CallbackResponse as GeneratedCallbackResponse,
  OAuthIdentityInfo as GeneratedOAuthIdentityInfo,
  OAuthIntent as GeneratedOAuthIntentType,
  OAuthProvider as GeneratedOAuthProviderType,
  OAuthResponseStatus as GeneratedOAuthResponseStatusType,
  UserInfo as GeneratedUserInfo,
} from './api/generated';

/**
 * Facade names for generated OAuth provider identifiers.
 */
export const OAUTH_PROVIDER = {
  Google: GeneratedOAuthProvider.google,
  Meta: GeneratedOAuthProvider.meta,
} as const;

export type OAuthProvider = GeneratedOAuthProviderType;

/** Narrows unknown values so callers can safely branch without casting. */
function isOAuthProvider(value: unknown): value is OAuthProvider {
  return (
    typeof value === 'string' && Object.values(OAUTH_PROVIDER).includes(value as OAuthProvider)
  );
}

/** Coerces URL query params into a typed provider; returns null for unsupported values. */
export function stringToProvider(providerStr?: string | null): OAuthProvider | null {
  return isOAuthProvider(providerStr) ? providerStr : null;
}

/** Application-facing names for the generated OAuth flow intents. */
export const OAuthIntent = {
  Login: GeneratedOAuthIntent.login,
  Register: GeneratedOAuthIntent.register,
  LinkAccount: GeneratedOAuthIntent.link_account,
} as const;

export type OAuthIntent = GeneratedOAuthIntentType;

/** Narrows unknown values to the OAuth intents accepted by the backend. */
function isOAuthIntent(value: unknown): value is OAuthIntent {
  return typeof value === 'string' && Object.values(OAuthIntent).includes(value as OAuthIntent);
}

/** Defaults to Login for missing/unrecognized intents so the OAuth flow never stalls. */
export function stringToIntent(intentStr?: string): OAuthIntent {
  return isOAuthIntent(intentStr) ? intentStr : OAuthIntent.Login;
}

/**
 * Facade names for generated OAuth callback status identifiers.
 */
export const OAuthResponseStatus = {
  // Success cases
  LoginSuccess: GeneratedOAuthResponseStatus.login_success,
  AccountLinked: GeneratedOAuthResponseStatus.account_linked,

  // Expected "error" cases that need special handling
  OAuthIdentityConflict: GeneratedOAuthResponseStatus.oauth_identity_conflict,
  NoExistingAccount: GeneratedOAuthResponseStatus.no_existing_account,
  SessionExpired: GeneratedOAuthResponseStatus.session_expired,
  NotLoggedIn: GeneratedOAuthResponseStatus.not_logged_in,
  ProviderMismatch: GeneratedOAuthResponseStatus.provider_mismatch,
  InvalidState: GeneratedOAuthResponseStatus.invalid_state,
  RegistrationRequired: GeneratedOAuthResponseStatus.registration_required,
} as const;

export type OAuthResponseStatus = GeneratedOAuthResponseStatusType;

/** Narrows unknown values to callback statuses emitted by the backend. */
function isOAuthResponseStatus(value: unknown): value is OAuthResponseStatus {
  return (
    typeof value === 'string' &&
    Object.values(OAuthResponseStatus).includes(value as OAuthResponseStatus)
  );
}

/** Browser-validated identity projection with absent optional text represented by omission. */
export type OAuthIdentityInfo = Omit<GeneratedOAuthIdentityInfo, 'email' | 'name' | 'picture'> & {
  email?: string;
  name?: string;
  picture?: string;
};

/** Browser-validated callback user projection. */
type UserInfo = Omit<GeneratedUserInfo, 'email' | 'name'> & {
  email?: string;
  name?: string;
};

/** Browser-validated callback projection with nullable wire fields normalized to omission. */
type CallbackResponse = Omit<
  GeneratedCallbackResponse,
  'invite_code' | 'oauth_identity' | 'user_info'
> & {
  invite_code?: string;
  oauth_identity?: OAuthIdentityInfo;
  user_info?: UserInfo;
};

/** Callback result augmented with transport and contract-validation state. */
type OAuthCallbackResult = CallbackResponse & { rpcError: boolean };

/** Untyped callback payload shape prior to runtime validation. */
interface RawCallbackResponse {
  status?: unknown;
  intent?: unknown;
  provider?: unknown;
  user_info?: unknown;
  oauth_identity?: unknown;
  invite_code?: unknown;
  rpcError?: unknown;
}

/** Defensive narrowing — callback payloads can have arbitrary types per-field. */
function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** Treats either JSON null or an omitted property as an absent optional value. */
function isAbsent(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

/** Validates raw identity fields before trusting them as typed OAuthIdentityInfo. */
function normalizeOAuthIdentityInfo(rawIdentity: unknown): OAuthIdentityInfo | undefined {
  if (typeof rawIdentity !== 'object' || rawIdentity === null) {
    return undefined;
  }

  const candidate = rawIdentity as Record<string, unknown>;
  const provider = stringToProvider(optionalString(candidate.provider));
  const provider_user_id = optionalString(candidate.provider_user_id);

  if (!provider || !provider_user_id) {
    return undefined;
  }

  const email = optionalString(candidate.email);
  const name = optionalString(candidate.name);
  const picture = optionalString(candidate.picture);
  if (
    (!isAbsent(candidate.email) && email === undefined) ||
    (!isAbsent(candidate.name) && name === undefined) ||
    (!isAbsent(candidate.picture) && picture === undefined)
  ) {
    return undefined;
  }

  return {
    provider,
    provider_user_id,
    ...(email === undefined ? {} : { email }),
    ...(name === undefined ? {} : { name }),
    ...(picture === undefined ? {} : { picture }),
  };
}

/** Validates user information nested in a callback response. */
function normalizeUserInfo(rawUserInfo: unknown): UserInfo | undefined {
  if (typeof rawUserInfo !== 'object' || rawUserInfo === null) return undefined;

  const candidate = rawUserInfo as Record<string, unknown>;
  if (typeof candidate.username !== 'string' || typeof candidate.is_new_user !== 'boolean') {
    return undefined;
  }

  const email = optionalString(candidate.email);
  const name = optionalString(candidate.name);
  if (
    (!isAbsent(candidate.email) && email === undefined) ||
    (!isAbsent(candidate.name) && name === undefined)
  ) {
    return undefined;
  }

  return {
    username: candidate.username,
    is_new_user: candidate.is_new_user,
    ...(email === undefined ? {} : { email }),
    ...(name === undefined ? {} : { name }),
  };
}

/** Validates and normalizes the exact callback response emitted by Rust. */
function normalizeCallbackResponse(value: unknown): CallbackResponse | null {
  if (typeof value !== 'object' || value === null) return null;

  const raw = value as RawCallbackResponse;
  const provider = stringToProvider(optionalString(raw.provider));
  if (!isOAuthResponseStatus(raw.status) || !isOAuthIntent(raw.intent) || !provider) return null;

  const userInfo = isAbsent(raw.user_info) ? undefined : normalizeUserInfo(raw.user_info);
  const oauthIdentity = isAbsent(raw.oauth_identity)
    ? undefined
    : normalizeOAuthIdentityInfo(raw.oauth_identity);
  const inviteCode = isAbsent(raw.invite_code) ? undefined : optionalString(raw.invite_code);
  if (
    (!isAbsent(raw.user_info) && userInfo === undefined) ||
    (!isAbsent(raw.oauth_identity) && oauthIdentity === undefined) ||
    (!isAbsent(raw.invite_code) && inviteCode === undefined) ||
    raw.rpcError !== undefined
  ) {
    return null;
  }

  return {
    status: raw.status,
    intent: raw.intent,
    provider,
    ...(userInfo === undefined ? {} : { user_info: userInfo }),
    ...(oauthIdentity === undefined ? {} : { oauth_identity: oauthIdentity }),
    ...(inviteCode === undefined ? {} : { invite_code: inviteCode }),
  };
}

/** Kicks off the OAuth redirect — returns the provider's authorization URL or an rpcError flag. */
export async function initiateOAuthLogin(
  provider: OAuthProvider,
  intent: OAuthIntent,
  inviteCode?: string
): Promise<{ url: string; rpcError?: boolean }> {
  // Feedback breadcrumb: record the OAuth sign-in start attempt now; outcome is marked success/failure below.
  const action = beginAction(FEEDBACK_ACTION.START_OAUTH_SIGN_IN, { provider });

  try {
    const data = await generatedPrepareOauth({
      provider,
      intent,
      ...(inviteCode ? { invite_code: inviteCode } : {}),
    });
    action.succeeded();

    // The server provides a complete authorization URL which already includes
    // the state parameter for session tracking through the OAuth flow
    return {
      url: data.authorization_url,
      rpcError: false,
    };
  } catch (error) {
    const responseStatus =
      typeof error === 'object' && error !== null && 'status' in error
        ? Number(error.status)
        : undefined;
    action.failed({
      error: responseStatus ? `Server returned HTTP ${responseStatus}` : errorReason(error),
    });
    console.error('OAuth prepare error:', error);
    if (!responseStatus) throw error;
    return { url: '', rpcError: true };
  }
}

/**
 * Completes the OAuth exchange. Normalizes the raw backend response into a
 * typed CallbackResponse, handling edge cases like duplicate code redemption
 * and malformed identity payloads without throwing.
 */
export async function handleOAuthCallback(
  provider: OAuthProvider,
  oauth_code: string,
  state: string
): Promise<OAuthCallbackResult> {
  // Feedback breadcrumb: record the OAuth callback attempt now; outcome is marked success/failure below.
  const action = beginAction(FEEDBACK_ACTION.COMPLETE_OAUTH_SIGN_IN, { provider });

  // The state parameter from the provider is our session state key

  const requestBody: CallbackRequest = {
    provider,
    oauth_code,
    state, // The state parameter is our session key
  };

  let responseData: CallbackResponse;
  let hasInvalidCallbackContract = false;
  let responseText = '';
  let responseStatus = 200;
  let hasTransportError = false;
  let transportErrorMessage: string | undefined;
  let couldNotReadResponse = false;
  try {
    const rawResponseData: unknown = await generatedCompleteOauth(requestBody);
    responseText = JSON.stringify(rawResponseData);
    const normalizedResponse = normalizeCallbackResponse(rawResponseData);
    if (normalizedResponse === null) {
      hasInvalidCallbackContract = true;
      responseData = {
        status: OAuthResponseStatus.InvalidState,
        intent: OAuthIntent.Login,
        provider,
      };
    } else {
      responseData = normalizedResponse;
    }
  } catch (error: unknown) {
    hasTransportError = true;
    const hasResponseStatus = typeof error === 'object' && error !== null && 'status' in error;
    const responseReadFailed =
      typeof error === 'object' &&
      error !== null &&
      'responseReadFailed' in error &&
      error.responseReadFailed === true;
    responseStatus = hasResponseStatus ? Number(error.status) : 0;
    couldNotReadResponse = responseReadFailed;
    if (!hasResponseStatus && !responseReadFailed) {
      transportErrorMessage = errorReason(error);
    }
    const errorData =
      typeof error === 'object' && error !== null && 'data' in error ? error.data : undefined;
    responseText = errorData === undefined ? '' : JSON.stringify(errorData);
    const normalizedResponse = normalizeCallbackResponse(errorData);
    hasInvalidCallbackContract = normalizedResponse === null;
    responseData = normalizedResponse ?? {
      status: OAuthResponseStatus.InvalidState,
      intent: OAuthIntent.Login,
      provider,
    };
  }

  // Check for transport error or specific OAuth token exchange error
  const hasTokenExchangeError = responseText.includes('OAuth token exchange error');
  const isCodeAlreadyUsed =
    responseStatus === 409 ||
    responseText.includes('OAuth code already used') ||
    responseText.includes('code has been used') ||
    responseText.includes('invalid_grant');

  // Special case: if it's just a "code already used" error and we got a successful response,
  // don't treat it as an RPC error - this is a normal condition from duplicate exchanges
  if (isCodeAlreadyUsed && !hasInvalidCallbackContract) {
    console.warn('OAuth code already used, but we have valid response data. Proceeding normally.');
  }

  const result: OAuthCallbackResult = {
    ...responseData,
    rpcError:
      !isCodeAlreadyUsed || hasInvalidCallbackContract
        ? hasInvalidCallbackContract || hasTransportError || hasTokenExchangeError
        : false,
  };

  // Log errors for debugging
  if (result.rpcError) {
    action.failed({
      error: couldNotReadResponse
        ? 'Could not read the sign-in response'
        : transportErrorMessage
          ? transportErrorMessage
          : hasInvalidCallbackContract
            ? 'Sign-in response was malformed'
            : hasTokenExchangeError
              ? 'OAuth token exchange failed'
              : hasTransportError
                ? `Server returned HTTP ${responseStatus}`
                : // TODO(1): These fallback arms are currently unreachable because every
                  // rpcError path is classified above by malformed-contract, token-exchange,
                  // or transport checks, and the duplicate-code branch clears rpcError.
                  isCodeAlreadyUsed
                  ? 'OAuth code was already used'
                  : 'Sign-in failed',
    });
    console.error('OAuth callback error detected:');
    if (hasTransportError) {
      console.error(`- HTTP status: ${responseStatus}`);
    }
    if (hasTokenExchangeError) {
      console.error('- Token exchange error detected in response');
    }
    if (isCodeAlreadyUsed) {
      console.warn('- OAuth code already used (this may be due to duplicate requests)');
    }
    console.error('- Response text:', responseText);
  } else {
    action.succeeded();
  }

  return result;
}

// All state is managed server-side.  `state` is an id passed through
// the OAuth provider's state parameter and returned back to us in the callback.
